/* LA RICETTA CHE VA DAL BACKTEST AL BOT — e la prova che ci è arrivata intera.
 *
 * Questo file esiste per una ragione sola: `strategyParams` deve arrivare al
 * bot IDENTICO a com'era nel backtest. Non «equivalente», non «normalizzato»:
 * identico. Un oggetto con ventotto chiavi in cui una diventa stringa invece
 * che numero, o in cui una si perde, è un'altra strategia — che però continua a
 * chiamarsi come quella misurata, e che opera con denaro vero.
 *
 * Le tre difese, in ordine di importanza:
 *
 *  1. NON SI RICOSTRUISCE, SI COPIA. L'oggetto di partenza è quello che il
 *     server ha restituito. Si parte da `{ ...origine }` — stesse chiavi, stesso
 *     ordine, stessi valori per identità — e si sovrascrivono SOLO le chiavi che
 *     l'utente ha davvero toccato. Nessun `defaultParams` viene mai applicato
 *     sopra: i valori predefiniti di una strategia servono a chi parte da zero,
 *     mai a chi parte da una misura.
 *  2. IL TIPO NON CAMBIA. Una chiave numerica torna numero, una booleana torna
 *     booleana, una testuale torna testo. Il tipo di partenza è l'unico
 *     ammesso: un `"14"` al posto di `14` è il modo silenzioso di cambiare
 *     strategia.
 *  3. SI VERIFICA PRIMA DI SPEDIRE. `copiaParametri` non si limita a produrre
 *     l'oggetto: lo confronta con l'originale e restituisce le anomalie. Se ce
 *     n'è anche una, la schermata non crea il bot. È una rete che non dovrebbe
 *     mai raccogliere nulla, e proprio per questo va tesa.
 *
 * Il resto del file è vocabolario: cosa è cambiato rispetto al backtest, quanto
 * warm-up serve davvero, e il corpo della POST con i suoi campi obbligati.
 */

import type { Backtest, ChainId, Dex, Tradebot } from '../../api';
import { num } from '../../lib/formato';
import { normalizzaAsset, normalizzaValuta } from '../../lib/chiaveConfig';
import { warmupRichiesto } from '../../lib/warmup';
import { etichettaParametro } from './modello';

/* ==========================================================================
 * La ricetta: cosa il backtest consegna al bot
 * ======================================================================== */

/**
 * I sei campi che vengono dal backtest e che definiscono COSA fa il bot.
 * Tutto il resto (dove opera, con quanto, con quali chiavi) è scelta di chi
 * crea, e il backtest non ha modo di saperlo.
 */
export interface Ricetta {
  strategyName: string;
  strategyParams: Record<string, unknown>;
  asset: string;
  currency: string;
  candleSize: number;
  warmupPeriod: number;
}

/**
 * Perché da questo backtest non si può creare un bot, o `null` se si può.
 *
 * Senza `strategyParams` il bot nascerebbe con la strategia giusta e i
 * parametri sbagliati — cioè con i valori predefiniti del motore, che non sono
 * quelli misurati. È il caso in cui NON si crea, e lo si dice.
 */
export function motivoRicettaInutilizzabile(b: Backtest): string | null {
  if (b.strategyParams == null) {
    return `Il backtest #${b.id} non porta con sé i parametri della strategia: la riga a database ha strategyParams vuoto. Senza quei valori il bot userebbe i predefiniti del motore, che non sono quelli misurati qui.`;
  }
  if (Object.keys(b.strategyParams).length === 0) {
    return `Il backtest #${b.id} ha strategyParams vuoto: nessun parametro da copiare. Il bot nascerebbe con i predefiniti del motore, non con la configurazione misurata.`;
  }
  if (!b.strategyName) {
    return `Il backtest #${b.id} non dichiara quale strategia ha misurato.`;
  }
  return null;
}

/** La ricetta di un backtest. Da chiamare solo dopo `motivoRicettaInutilizzabile`. */
export function ricettaDaBacktest(b: Backtest): Ricetta {
  return {
    strategyName: b.strategyName,
    /* Il riferimento all'oggetto del server, non una copia: la copia si fa una
       volta sola, in `copiaParametri`, dove è anche verificata. */
    strategyParams: b.strategyParams as Record<string, unknown>,
    asset: b.asset,
    currency: b.currency,
    candleSize: b.candleSize,
    warmupPeriod: b.warmupPeriod,
  };
}

/* ==========================================================================
 * I parametri, uno per uno
 * ======================================================================== */

export type TipoValore = 'numero' | 'booleano' | 'testo' | 'composto';

export interface ParametroRicetta {
  chiave: string;
  etichetta: string;
  tipo: TipoValore;
  /** Il valore del backtest, per identità. Non si tocca mai. */
  originale: unknown;
  /** Come si legge nel backtest. Stessa resa del dettaglio del backtest, così
   *  i due schermi si confrontano a occhio senza tradurre. */
  testo: string;
}

function tipoDi(v: unknown): TipoValore {
  if (typeof v === 'number') return 'numero';
  if (typeof v === 'boolean') return 'booleano';
  if (typeof v === 'string') return 'testo';
  return 'composto';
}

/** Identica a quella del dettaglio del backtest: `null` → «—», oggetto → JSON. */
export function testoValore(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * I parametri nell'ordine in cui il server li ha scritti.
 *
 * L'ordine non è estetica: è l'ordine con cui compaiono nel dettaglio del
 * backtest, ed è l'unico modo di confrontare le due schermate riga per riga
 * senza cercare ogni nome.
 */
export function elencoParametri(params: Record<string, unknown>): ParametroRicetta[] {
  return Object.entries(params).map(([chiave, originale]) => ({
    chiave,
    etichetta: etichettaParametro(chiave),
    tipo: tipoDi(originale),
    originale,
    testo: testoValore(originale),
  }));
}

/* ==========================================================================
 * Bozze: cosa c'è nei campi mentre si digita
 * ======================================================================== */

/** I numeri restano stringhe finché si digita: cancellare l'ultima cifra
 *  altrimenti farebbe saltare il campo a zero. */
export type Bozza = string | boolean;

export function bozzaIniziale(p: ParametroRicetta): Bozza {
  if (p.tipo === 'booleano') return p.originale as boolean;
  if (p.tipo === 'composto') return p.testo;
  return String(p.originale);
}

/** Un parametro composto (oggetto, lista, `null`) non si modifica da qui: si
 *  mostra e si copia com'è. Nasconderlo lo farebbe cercare altrove. */
export function modificabile(p: ParametroRicetta): boolean {
  return p.tipo !== 'composto';
}

export function bozzaValida(p: ParametroRicetta, b: Bozza | undefined): boolean {
  if (!modificabile(p)) return true;
  if (p.tipo === 'booleano') return typeof b === 'boolean';
  if (typeof b !== 'string') return false;
  if (p.tipo === 'testo') return true;
  return b.trim() !== '' && Number.isFinite(Number(b.replace(',', '.')));
}

/**
 * Il parametro è stato davvero cambiato?
 *
 * Il confronto sui numeri è numerico e non testuale: `5` e `5.0` sono lo stesso
 * valore, e contarli come modifica farebbe apparire uno scostamento dal
 * backtest che non esiste — cioè un allarme falso proprio sull'allarme che deve
 * restare credibile.
 */
export function eDiverso(p: ParametroRicetta, b: Bozza | undefined): boolean {
  if (b === undefined || !modificabile(p)) return false;
  if (p.tipo === 'booleano') return b !== p.originale;
  if (typeof b !== 'string') return false;
  if (p.tipo === 'testo') return b !== (p.originale as string);
  const n = Number(b.replace(',', '.'));
  if (!Number.isFinite(n)) return true;
  return n !== (p.originale as number);
}

/** Il valore da scrivere, NEL TIPO DI PARTENZA. Mai un tipo diverso. */
export function valoreDaBozza(p: ParametroRicetta, b: Bozza | undefined): unknown {
  if (b === undefined || !modificabile(p)) return p.originale;
  if (p.tipo === 'booleano') return b === true;
  if (typeof b !== 'string') return p.originale;
  if (p.tipo === 'testo') return b;
  const n = Number(b.replace(',', '.'));
  return Number.isFinite(n) ? n : p.originale;
}

/* ==========================================================================
 * LA COPIA, e la sua verifica
 * ======================================================================== */

export interface EsitoCopia {
  /** L'oggetto da spedire. Da usare solo se `anomalie` è vuoto. */
  valori: Record<string, unknown>;
  /** Le chiavi che l'utente ha cambiato rispetto al backtest. */
  toccate: string[];
  /** Le chiavi il cui campo non contiene un valore utilizzabile. */
  invalide: string[];
  /**
   * Differenze fra l'oggetto prodotto e quello di partenza che NON sono
   * spiegate da una modifica dell'utente. Dovrebbe essere sempre vuoto: se non
   * lo è, c'è un difetto in questo file e il bot non va creato.
   */
  anomalie: string[];
}

const SEPARATORE_CHIAVI = '';

/**
 * Copia i parametri e verifica di averlo fatto.
 *
 * Il punto di partenza è `{ ...origine }`: stesse chiavi, stesso ordine di
 * inserimento, stessi valori per identità — comprese le strutture annidate, che
 * passano per riferimento e quindi non possono essere alterate da una
 * serializzazione andata storta. Poi si sovrascrivono le sole chiavi toccate.
 *
 * Nessun filtro, nessun riordino, nessun valore predefinito: le tre cose che
 * questa funzione non fa sono la ragione per cui esiste.
 */
export function copiaParametri(
  origine: Record<string, unknown>,
  parametri: ParametroRicetta[],
  bozze: Record<string, Bozza>,
): EsitoCopia {
  const valori: Record<string, unknown> = { ...origine };
  const toccate: string[] = [];
  const invalide: string[] = [];

  for (const p of parametri) {
    const b = bozze[p.chiave];
    if (!bozzaValida(p, b)) {
      invalide.push(p.chiave);
      continue;
    }
    if (!eDiverso(p, b)) continue;
    toccate.push(p.chiave);
    valori[p.chiave] = valoreDaBozza(p, b);
  }

  return { valori, toccate, invalide, anomalie: anomalieCopia(origine, valori, toccate) };
}

/**
 * Cosa è cambiato senza che nessuno l'abbia chiesto.
 *
 * Tre controlli, tutti su ciò che è costato caro altrove: chiavi perse o
 * inventate, valori mutati su chiavi mai toccate, tipo cambiato su chiavi
 * toccate. Anche l'ordine viene confrontato — non cambia il comportamento del
 * motore, ma un ordine diverso significa che l'oggetto è stato ricostruito
 * invece che copiato, e allora tutto il resto va riguardato.
 */
export function anomalieCopia(
  origine: Record<string, unknown>,
  finali: Record<string, unknown>,
  toccate: string[],
): string[] {
  const anomalie: string[] = [];
  const chiaviOrigine = Object.keys(origine);
  const chiaviFinali = Object.keys(finali);

  const perse = chiaviOrigine.filter((k) => !(k in finali));
  if (perse.length > 0) anomalie.push(`parametri persi per strada: ${perse.join(', ')}`);

  const aggiunte = chiaviFinali.filter((k) => !(k in origine));
  if (aggiunte.length > 0) anomalie.push(`parametri inventati: ${aggiunte.join(', ')}`);

  if (
    perse.length === 0 &&
    aggiunte.length === 0 &&
    chiaviOrigine.join(SEPARATORE_CHIAVI) !== chiaviFinali.join(SEPARATORE_CHIAVI)
  ) {
    anomalie.push("i parametri hanno cambiato ordine: l'oggetto è stato ricostruito invece che copiato");
  }

  const insiemeToccate = new Set(toccate);
  for (const k of chiaviOrigine) {
    if (!(k in finali)) continue;
    if (insiemeToccate.has(k)) {
      if (typeof finali[k] !== typeof origine[k]) {
        anomalie.push(
          `«${k}» ha cambiato tipo: era ${typeof origine[k]}, sarebbe ${typeof finali[k]}`,
        );
      }
      continue;
    }
    if (!Object.is(finali[k], origine[k])) {
      anomalie.push(`«${k}» è cambiato senza essere stato modificato`);
    }
  }

  return anomalie;
}

/* ==========================================================================
 * Scostamenti dal backtest
 * ======================================================================== */

export interface Scostamento {
  chiave: string;
  campo: string;
  da: string;
  a: string;
  /**
   * Diverso solo nel nome: `BTCB` su BSC è il `BTC` del backtest, `USDC` è la
   * `USDT` del backtest. Il bot corrisponde ancora alla misura; cambia il
   * ticker con cui la catena chiama la stessa cosa.
   */
  soloNominale: boolean;
}

/** I campi della ricetta che si scrivono a mano, ancora in forma di testo. */
export interface FormaRicetta {
  asset: string;
  currency: string;
  candleSize: string;
  warmupPeriod: string;
}

/**
 * Cosa NON corrisponde più al backtest.
 *
 * `warmupPeriod` è deliberatamente FUORI da questo elenco. Nei backtest è spesso
 * gonfiato apposta per allineare timeframe diversi — quelli a un'ora ne portano
 * quattro volte quelli a quattro ore, così le due misure partono dallo stesso
 * punto della finestra reale — e per un bot dal vivo quel gonfiaggio non serve a
 * niente: serve solo che gli indicatori siano formati prima della prima
 * decisione. Ridurlo non stacca il bot dalla misura: il bot 12 è passato da
 * 6.800 a 3.000 senza che il risultato cambiasse. Il warm-up ha una sorveglianza
 * sua, in `stimaWarmup`, e riguarda una cosa diversa: stare sopra il minimo.
 */
export function scostamentiRicetta(
  ricetta: Ricetta,
  forma: FormaRicetta,
  parametri: ParametroRicetta[],
  bozze: Record<string, Bozza>,
): Scostamento[] {
  const fuori: Scostamento[] = [];

  const asset = forma.asset.trim();
  if (asset && asset.toUpperCase() !== ricetta.asset.toUpperCase()) {
    fuori.push({
      chiave: 'asset',
      campo: 'Asset',
      da: ricetta.asset,
      a: asset,
      soloNominale: normalizzaAsset(asset) === normalizzaAsset(ricetta.asset),
    });
  }

  const currency = forma.currency.trim();
  if (currency && currency.toUpperCase() !== ricetta.currency.toUpperCase()) {
    fuori.push({
      chiave: 'currency',
      campo: 'Valuta di quotazione',
      da: ricetta.currency,
      a: currency,
      soloNominale: normalizzaValuta(currency) === normalizzaValuta(ricetta.currency),
    });
  }

  const candela = Number(forma.candleSize);
  if (Number.isFinite(candela) && candela > 0 && candela !== ricetta.candleSize) {
    fuori.push({
      chiave: 'candleSize',
      campo: 'Candela',
      da: `${ricetta.candleSize} min`,
      a: `${candela} min`,
      soloNominale: false,
    });
  }

  for (const p of parametri) {
    if (!eDiverso(p, bozze[p.chiave])) continue;
    fuori.push({
      chiave: `strategyParams.${p.chiave}`,
      campo: p.etichetta,
      da: p.testo,
      a: testoValore(valoreDaBozza(p, bozze[p.chiave])),
      soloNominale: false,
    });
  }

  return fuori;
}

/* ==========================================================================
 * Quanto warm-up serve davvero
 * ======================================================================== */

export interface StimaWarmup {
  /** Sotto questo numero di candele gli indicatori non sono formati. */
  minimo: number;
  /** Il minimo con il 10% di margine, arrotondato a un numero pulito. */
  consigliato: number;
  /** Come è stato ricavato, per scriverlo accanto al campo. */
  formula: string;
}

/**
 * Le strategie in cui la finestra più lunga si chiama `N1` e `r`.
 *
 * Tutte e cinque calcolano la quota su tre finestre — `N1`, `r·N1`, `r²·N1` — e
 * la più lunga è quella che decide: finché non ci sono `r²·N1` candele alle
 * spalle, la terza finestra non è formata.
 *
 * Serve un elenco per nome perché l'euristica generale (`lib/warmup.ts`, la
 * stessa del backend) riconosce le finestre dal NOME del parametro — `*Period`,
 * `*length`, `*window` — e `N1` non le somiglia. Su queste cinque strategie
 * l'euristica vede solo `ROC_period` e `RSI_period`, poche decine di candele, e
 * sottostima di due ordini di grandezza il vincolo vero.
 */
const FAMIGLIA_QUOTA = new Set([
  'SEM26C',
  'SEM26C2',
  'SEM26C3',
  'SEM26CROCRSI',
  'SEMQuota26',
]);

/** La terza finestra della quota, `N1 × r²`, o `null` fuori dalla famiglia. */
function finestraQuota(strategyName: string, params: Record<string, unknown>): number | null {
  if (!FAMIGLIA_QUOTA.has(strategyName)) return null;
  const N1 = Number(params.N1);
  const r = Number(params.r);
  if (!Number.isFinite(N1) || !Number.isFinite(r) || N1 <= 0 || r <= 0) return null;
  return Math.ceil(N1 * r * r);
}

/** Arrotondamento a un numero che si legge: 2.964 → 3.000, 302 → 350. */
function arrotondaSu(n: number): number {
  const passo = n >= 1000 ? 100 : n >= 200 ? 50 : 10;
  return Math.ceil(n / passo) * passo;
}

/**
 * Il warm-up minimo per una configurazione, o `null` se non lo sappiamo.
 *
 * Due fonti, e vince la più esigente — perché sottostimare è l'unico errore che
 * costa: un warm-up troppo alto è solo qualche candela in più da scaricare, uno
 * troppo basso dà un risultato che sembra migliore di quello vero.
 *
 *  - l'euristica sui nomi di `lib/warmup.ts`, che è la stessa del backend e
 *    vale per tutte le strategie;
 *  - la finestra della quota, per le cinque strategie in cui quell'euristica
 *    guarda dalla parte sbagliata.
 *
 * Restituire `null` è una risposta buona quanto un numero: quando nessuna delle
 * due fonti riconosce una finestra, un minimo inventato autorizzerebbe a
 * scendere sotto la soglia vera — che è il danno da cui questa stima difende.
 */
export function stimaWarmup(
  strategyName: string,
  params: Record<string, unknown>,
): StimaWarmup | null {
  const generale = warmupRichiesto(params);
  const quota = finestraQuota(strategyName, params);

  const minimo = Math.max(generale.minimo, quota ?? 0);
  if (minimo <= 0) return null;

  const consigliato = arrotondaSu(minimo * 1.1);

  const N1 = Number(params.N1);
  const r = Number(params.r);
  const daQuota = quota != null && quota >= generale.minimo;

  const origine = daQuota
    ? `la finestra più lunga della quota è N1 × r² = ${N1} × ${r}² = ${minimo} candele`
    : `il parametro più lungo è ${generale.cause[0].chiave} = ${num(generale.cause[0].candele)} candele`;

  return {
    minimo,
    consigliato,
    formula: `${origine}; con il 10% di margine si arriva a ${num(consigliato)}`,
  };
}

/* ==========================================================================
 * Il corpo di POST /tradebots
 * ======================================================================== */

export interface SedeCreazione {
  tipo: 'account' | 'wallet';
  id: number;
}

export interface ConfigurazioneDex {
  dex: Dex;
  chainId: ChainId;
  rpc: string;
  gasLimit: string;
  swapSlippage: string;
  minimumBaseCoinBalance: string;
  rechargeQtyBaseCoinBalance: string;
}

export interface DatiCreazione {
  strategyName: string;
  strategyParams: Record<string, unknown>;
  asset: string;
  currency: string;
  candleSize: number;
  warmupPeriod: number;
  marketWatcherId: number;
  sede: SedeCreazione;
  capitale: number;
  usePartialBalance: boolean;
  useRealMoney: boolean;
  avviaSubito: boolean;
  telegramToken: string;
  telegramPassword: string;
  /** `null` per un bot su exchange. */
  dex: ConfigurazioneDex | null;
}

/**
 * Un campo numerico vuoto NON è uno zero: è un `null`.
 *
 * Le colonne del tradebot sono `float` e `enum`: Postgres rifiuta la stringa
 * vuota con «invalid input syntax for type double precision», che arriva al
 * browser come un 500 nudo. È lo stesso difetto corretto di recente sul
 * frontend attuale, e va tenuto corretto qui — un campo lasciato vuoto è la
 * cosa più normale che possa succedere in un modulo con venti campi.
 */
function numeroONull(v: string | number | null | undefined): number | null {
  if (v === '' || v == null) return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function testoONull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

/**
 * Il corpo della creazione.
 *
 * Due cose che il server fa di sua iniziativa e che vanno contraddette qui:
 *
 *  - `isRunning` ha `default: true` sulla entity. Ometterlo significa creare un
 *    bot GIÀ IN MARCIA, e su denaro vero significa comprare senza che nessuno
 *    l'abbia chiesto. Si manda sempre, esplicito, anche quando vale `false`.
 *  - `dex` ha `default: ZEROX`. Per un bot su exchange non vuol dire niente e
 *    non si manda: `walletId` nullo è ciò che distingue i due mondi, e un `dex`
 *    scritto su un bot CEX è un campo che qualcuno prima o poi leggerà.
 *
 * `ValidationPipe` gira senza `whitelist`: i campi in più non vengono scartati
 * dal server, quindi qui non se ne mettono.
 */
export function corpoCreazione(d: DatiCreazione): Partial<Tradebot> {
  const corpo: Record<string, unknown> = {
    /* La ricetta. `strategyParams` è già l'oggetto verificato da
       `copiaParametri`: qui non lo si tocca più. */
    strategyName: d.strategyName,
    strategyParams: d.strategyParams,
    asset: d.asset.trim().toUpperCase(),
    currency: d.currency.trim().toUpperCase(),
    candleSize: numeroONull(d.candleSize),
    warmupPeriod: numeroONull(d.warmupPeriod),

    marketWatcherId: numeroONull(d.marketWatcherId),

    useRealMoney: d.useRealMoney,
    // Il flag si accende solo sui conti di exchange: su catena non viene letto da nessuna
    // parte, e mandarlo acceso registrerebbe un tetto che nessuno fa rispettare.
    usePartialBalance: d.sede.tipo === 'wallet' ? false : d.usePartialBalance,
    isRunning: d.avviaSubito,

    /* Il capitale finisce in `currencyBalance`: è l'unico modo che il backend
       conosce per limitare quanto un bot può muovere, e vale solo insieme a
       `usePartialBalance`. La storia parte da zero: nessun asset in mano. */
    currencyBalance: numeroONull(d.capitale),
    assetBalance: 0,

    telegramToken: testoONull(d.telegramToken),
    telegramPassword: testoONull(d.telegramPassword),
  };

  if (d.sede.tipo === 'account') {
    corpo.accountId = d.sede.id;
    corpo.walletId = null;
    /* Su exchange i campi di catena non hanno significato: si azzerano invece
       di lasciarli fuori, così un bot CEX non porta con sé un RPC o un gas
       limit ereditati da un valore predefinito. */
    corpo.chainId = null;
    corpo.rpc = null;
    corpo.gasLimit = null;
    corpo.swapSlippage = null;
    corpo.minimumBaseCoinBalance = null;
    corpo.rechargeQtyBaseCoinBalance = null;
  } else {
    corpo.walletId = d.sede.id;
    corpo.accountId = null;
    const c = d.dex;
    corpo.dex = c?.dex ?? null;
    corpo.chainId = c ? c.chainId : null;
    corpo.rpc = c ? testoONull(c.rpc) : null;
    corpo.gasLimit = numeroONull(c?.gasLimit ?? null);
    corpo.swapSlippage = numeroONull(c?.swapSlippage ?? null);
    corpo.minimumBaseCoinBalance = numeroONull(c?.minimumBaseCoinBalance ?? null);
    corpo.rechargeQtyBaseCoinBalance = numeroONull(c?.rechargeQtyBaseCoinBalance ?? null);
  }

  return corpo as Partial<Tradebot>;
}

/**
 * Lo slippage predefinito per i bot nuovi, in percentuale.
 *
 * `null` significa «lo decide il backend» (0,5%), che è il comportamento
 * storico di 0x e 1inch. Su Velora si parte da 0,15%: il costo reale misurato
 * su 10.000 USDT → BTCB è di 2,13 punti base, quindi 50 punti base non sono una
 * tolleranza ma un invito al sandwich.
 */
export const SLIPPAGE_PREDEFINITO: Record<Dex, number | null> = {
  ZEROX: null,
  ONEINCH: null,
  VELORA: 0.15,
};

export const ETICHETTE_DEX: Record<Dex, string> = {
  ZEROX: '0x',
  ONEINCH: '1inch',
  VELORA: 'Velora',
};
